import { useMemo, ReactNode } from "react"
import { GetApp } from "../../App"
import { GetDay } from "../../App/Context"
import { CloseButton } from "../../Components/CloseButton"
import { DetailsToggle } from "../../Components/DetailsToggle"
import { Icon, Icons } from "../../Components/Icon"
import { applyFieldFormatEntry, FieldFormat } from "../../App/Format"
import { IDayCalculations, ILog, IStatus, IStatusType, IShipStatus } from "../../Types"
import { dayToDate } from "./Context"
import { PreviousDayProps } from "./Params"

export interface DayHeaderTotals {
  distance: number;
  duration: number;
  sog: number;
}

/**
 * Pure derivations for the day headline. Grouped here (no React/DOM) so the
 * distance/duration/sog logic can be unit-tested in isolation.
 */
export const DayHeaderViewData = {
  /** The open (not-yet-ended) ship status, if any. */
  shipStatus(statuses: IStatus[]): IStatus | undefined {
    return statuses.filter((s) => s.statusType == IStatusType.ship).find((s) => s.endLogId == undefined);
  },

  /** Whether the live current log should extend the day totals (only while moving/drifting on the current day). */
  includeCurrentLogInStatus(shipStatus: IStatus | undefined, isCurrentDay: boolean): boolean {
    if (!isCurrentDay || !shipStatus) {
      return false;
    }
    return shipStatus.value == IShipStatus.inmotion || shipStatus.value == IShipStatus.drifting;
  },

  /**
   * Headline distance/duration/sog. Starts from the stored day calculation and,
   * while under way, extends it by the delta between the last committed log and
   * the live current log (both carry a cumulative `distance` odometer).
   */
  totals(calculated: IDayCalculations | undefined, logs: ILog[], currentLog: ILog | undefined, includeCurrentLog: boolean): DayHeaderTotals {
    let distance = calculated?.distance || 0;
    let duration = calculated?.duration || 0;
    let sog = calculated?.sog || 0;
    if (includeCurrentLog && logs && logs.length > 0 && currentLog) {
      const lastLog = logs.slice(-1)[0];
      const extraDistance = currentLog.distance ? (currentLog.distance - (lastLog.distance || 0)) : 0;
      const extraDuration = currentLog.timeUtc ? (currentLog.timeUtc - lastLog.timeUtc) : 0;
      distance += extraDistance;
      duration += extraDuration;
      sog = duration > 0 ? (distance / duration * 3600) : sog;
    }
    return { distance, duration, sog };
  },

  // Per-field value formatters for `DayHeaderTotals` (issue #529 consistency pass):
  // mirrors this view's own `app.format(...)` calls, so distance/duration/sog are
  // always formatted identically wherever these totals are displayed.
  fieldFormat(): FieldFormat {
    return {
      distance: "distance",
      duration: "time",
      sog: "speed",
    };
  },

  /**
   * Hour-of-day span for the timeline "active hours" highlight.
   *
   * On the current day the logs (plus the live current log) are loaded and
   * moving, so the span is derived from them. For any other day the logs are
   * lazily loaded only once the day is expanded, so deriving the span from
   * `logs` left a collapsed row pinned to hour 0 until it was opened (issue
   * #476). Those days instead use the pre-calculated motion span already carried
   * by the stored day calculation (`startTime`/`endTime`), which is available
   * whether or not the logs have been fetched and stays consistent on open.
   *
   * `hasRange` is false when no source has an active span, so the caller can hide
   * the highlight entirely (e.g. a day spent in port with no motion).
   */
  hourRange(logs: ILog[], currentLog?: ILog, calculated?: IDayCalculations, isCurrentDay?: boolean): { minHour: number; maxHour: number; hasRange: boolean } {
    if (!isCurrentDay && calculated && calculated.startTime != undefined && calculated.endTime != undefined) {
      return { minHour: Math.floor(calculated.startTime / 3600), maxHour: Math.floor(calculated.endTime / 3600), hasRange: true };
    }
    let minHour: number | undefined = undefined;
    let maxHour: number | undefined = undefined;
    const allLogs: ILog[] = logs ? logs.slice() : [];
    if (currentLog) {
      allLogs.push(currentLog);
    }
    for (const log of allLogs) {
      const hour = Math.floor(log.time / 3600);
      if (minHour == undefined || hour < minHour) {
        minHour = hour;
      }
      if (maxHour == undefined || hour > maxHour) {
        maxHour = hour;
      }
    }
    return { minHour: minHour || 0, maxHour: maxHour || 0, hasRange: minHour != undefined };
  },
};

interface DayHeaderViewProps {
  advices?: ReactNode[];
  currentLog?: ILog;
  details: boolean;
  isCurrentDay: boolean;
  isTomorrow: boolean;
  isYesterday: boolean;
  logs: ILog[];
  selected: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  setDetails: any;
  previousDay?: PreviousDayProps;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  toggleSelected: any;
}

export function DayHeaderView({ advices, currentLog, details: _details, isCurrentDay, isTomorrow, isYesterday, logs, selected, setDetails, previousDay, toggleSelected }: DayHeaderViewProps) {
  const app = GetApp();
  const { day, statuses } = GetDay();
  const t = app.getT("DayHeaderView");
  const calculated = !isTomorrow ? day.calculated : undefined;

  const date = useMemo((): Date => {
    if (day.day <= 1) {
      const date = new Date();
      const now = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
      const today = Math.floor(now.valueOf() - new Date(Date.UTC(2001, 0, 1)).valueOf()) / (60 * 60 * 24 * 1000);
      if (isCurrentDay) {
        return dayToDate(today);
      }
      if (isTomorrow) {
        return dayToDate(today + 1);
      }
    }
    return dayToDate(day.day);
  }, [day, isCurrentDay, isTomorrow]);

  const shipStatus = useMemo(() => {
    return DayHeaderViewData.shipStatus(statuses);
  }, [statuses]);

  const includeCurrentLogInStatus = useMemo(() => {
    return DayHeaderViewData.includeCurrentLogInStatus(shipStatus, isCurrentDay);
  }, [shipStatus, isCurrentDay]);

  // const timelineStatuses = useMemo(() => {
  //   if (logs || currentLog) {
  //     return shipStatuses.map((s) => {
  //       const startLog = logs.find((l) => l.id == s.startLogId);
  //       const endLog = logs.find((l) => l.id == s.endLogId);
  //       const startHour = startLog.time / 3600;
  //       const endHour = endLog ? (endLog.time / 3600) : (isCurrentDay && currentLog && (currentLog.time / 3600));
  //       return {start: startHour, end: endHour, status: IShipStatus[s.value]};
  //     });
  //   }
  //   return []
  // }, [shipStatuses, isCurrentDay, currentLog]);

  if (currentLog) {
    currentLog.nmeaPending = true;
  }
  const { distance, duration, sog } = DayHeaderViewData.totals(calculated, logs, currentLog, includeCurrentLogInStatus);
  const { minHour, maxHour, hasRange } = DayHeaderViewData.hourRange(logs, currentLog, calculated, isCurrentDay);
  const fieldFormat = useMemo(() => DayHeaderViewData.fieldFormat(), []);

  const moonphaseIcon = useMemo(() => {
    const m = day.calculated?.moonphase;
    const cs = 1 / 29.5;
    if (!m) {
      return null;
    }
    if (m >= 0.0 && m < cs) {
      return <Icon className="DayView-moonphase" icon={Icons.MoonNew} />;
    } else if (m >= 0.25 && m < 0.25 + cs) {
      return <Icon className="DayView-moonphase" icon={Icons.MoonWaxing} />;
    } else if (m >= 0.5 && m < 0.5 + cs) {
      return <Icon className="DayView-moonphase" icon={Icons.MoonFull} />;
    } else if (m >= 0.75 && m < 0.75 + cs) {
      return <Icon className="DayView-moonphase" icon={Icons.MoonWaning} />;
    }
    return null;
  }, [day]);

  const startLocation = useMemo(() => {
    return (day.startLocation || "").length > 0 ? day.startLocation : previousDay?.startLocation;
  }, [previousDay, day]);

  const dayMarkers = [4, 8, 12, 16, 20].map((h) => <div key={`hour-${h}`} className={"hour hour-" + h}></div>);

  return (
    <div className="DayHeaderView">
      <div className="DayView-date" onClick={isCurrentDay ? undefined : toggleSelected}>
        {(day.day != 0 || isCurrentDay) && (
          <>
            {moonphaseIcon}{app.format("dateInYear", date.toISOString())}
          </>
        )}
      </div>
      <div className="DayView-timeline">
        {selected && !isCurrentDay && !isTomorrow && day.id ? <DetailsToggle className={"DayView-toggle"} onChange={setDetails} /> : <>{!isCurrentDay && (!isTomorrow || !selected) && dayMarkers}{hasRange && <div className="fill" style={{ marginTop: `${(minHour / 24) * 200}%`, height: `${(maxHour + 1 - minHour) / 24 * 100}%` }}></div>}</>}
      </div>
      <div className="DayView-cell DayView-comment" onClick={isCurrentDay ? undefined : toggleSelected}>
        {isCurrentDay && <em className="comment">{t("today")}</em>}
        {isTomorrow && <em className="comment">{t("nextDay")}</em>}
        {isYesterday && <em className="comment">{t("yesterday")}</em>}
        {(startLocation || day.endLocation) && <>{startLocation} ⋯ <strong>{day.endLocation}</strong></>}
        {day.comment && <> - <span className="comment">{day.comment}</span></>}
        {isCurrentDay && advices}
        {/* <span className="debug-data">{["day:", day.day, "dayId:", day.id, "tripId:", day.tripId, "cruiseId:", day.cruiseId].join(" ")}</span> */}
      </div>

      {!isTomorrow && (
        <>
          <div className="DayView-cell DayView-duration">{applyFieldFormatEntry(fieldFormat.duration, duration, app.format)}</div>
          <div className="DayView-cell DayView-speed">{applyFieldFormatEntry(fieldFormat.sog, sog, app.format)}</div>
          <div className="DayView-cell DayView-distance"><strong>{applyFieldFormatEntry(fieldFormat.distance, distance, app.format)}</strong></div>
        </>
      )}
      <div className="DayView-gap">
        {selected && !isCurrentDay && <CloseButton iconClassName="button" onClick={toggleSelected} />}
      </div>
    </div>
  );
}
